import type { PurchaseRequestConfirmationData } from "@fero/pages";
import type { BigDecimal, LocalDate } from "adl-gen/common";
import type {
  DeliveryFee,
  DutyAndVat,
  PurchaseRequestDeliveryOption,
  PurchaseRequestSalePriceType,
  PurchaseRequestState,
  PurchaseRequestStateEvent,
} from "adl-gen/ferovinum/app/db";
import type { AdditionalCost } from "adl-gen/ferovinum/app/nompurchaser";
import type { FeroChipVariant } from "components/widgets";
import { formatDate } from "utils/date-utils";
import { assertNever } from "utils/hx/util/types";
import { ensure } from "utils/null-and-undefined-utils";
import { toPrecision } from "utils/numeric-utils";

export function getPurchaseRequestStateDescription({
  stateEvents,
  collectionDays,
  purchaseRequestDeliveryOption,
  purchaserPaidDate,
  orgNetReceivablePaymentDate,
  collectedDate,
  deliveredDate,
  productionOrderNumber,
}: {
  stateEvents: PurchaseRequestStateEvent[];
  collectionDays: number;
  purchaseRequestDeliveryOption: PurchaseRequestDeliveryOption;
  purchaserPaidDate: LocalDate | null;
  orgNetReceivablePaymentDate: LocalDate | null;
  collectedDate: LocalDate | null;
  deliveredDate: LocalDate | null;
  productionOrderNumber?: string;
}) {
  const lastStateEvent = stateEvents[stateEvents.length - 1];
  const { state, time } = lastStateEvent;
  const eventDate = formatDate(new Date(time));
  const isCollection =
    purchaseRequestDeliveryOption.kind === "collectFromStorageLocation";
  switch (state) {
    case "new":
      return `Request made on ${eventDate}`;
    case "purchaserAccepted":
    case "productionOrderComplete":
      return `Accepted by the purchaser on ${eventDate}`;
    case "readyForCollection":
      return `Marked as ready for collection on ${eventDate}`;
    case "collected":
      return `Collected on ${collectedDate ? formatDate(new Date(collectedDate)) : eventDate}`;
    case "delivered":
      return `${isCollection ? "Collected" : "Delivered"} on ${
        deliveredDate ? formatDate(new Date(deliveredDate)) : eventDate
      }`;
    case "purchaserInvoiced":
      return `Purchaser invoiced on ${eventDate}`;
    case "purchaserPaid":
      return `Purchaser paid ${purchaserPaidDate ? `on ${formatDate(new Date(purchaserPaidDate))}` : ""}`;
    case "organisationPaid":
      return `You were paid ${
        orgNetReceivablePaymentDate
          ? `on ${formatDate(new Date(orgNetReceivablePaymentDate))}`
          : ""
      }`;
    case "purchaserRejected":
      return `Sale order was rejected by purchaser on ${eventDate}`;
    case "cancelled":
      return getPurchaseRequestLongCancellationReason(
        stateEvents,
        collectionDays,
        productionOrderNumber,
      );
    default:
      assertNever(state);
  }
}

function getPurchaseRequestLongCancellationReason(
  prStates: PurchaseRequestStateEvent[],
  collectionDays: number,
  productionOrderNumber?: string,
) {
  const stateEventBeforeExpiry = [...prStates].reverse()[1];
  const { state, time } = stateEventBeforeExpiry;
  const date = formatDate(new Date(time));

  let cancellationReason = `Cancelled on ${date} as `;
  switch (state) {
    case "purchaserAccepted": {
      if (productionOrderNumber !== undefined) {
        cancellationReason += `production order ${productionOrderNumber} was rejected or could not be completed in time`;
      } else {
        cancellationReason += "stock preparation took longer than expected";
      }
      break;
    }
    case "readyForCollection": {
      cancellationReason += `stock was not collected within ${collectionDays} days`;
      break;
    }
  }

  return cancellationReason;
}

export function getPurchaseRequestShortCancellationReason(
  prStates: PurchaseRequestStateEvent[],
  collectionDays: number,
) {
  const lastNonCancelledEvents = [...prStates].reverse()[1];

  let cancellationReason = "Cancelled: ";
  switch (lastNonCancelledEvents.state) {
    case "purchaserAccepted":
      cancellationReason += "preparation took longer than stated";
      break;
    case "readyForCollection":
      cancellationReason += `was not collected within ${collectionDays} days`;
      break;
  }
  return cancellationReason;
}

export function getIncoterms(
  purchaseRequestData: Pick<
    PurchaseRequestConfirmationData,
    "purchaseRequestDeliveryOption"
  >,
) {
  return purchaseRequestData.purchaseRequestDeliveryOption.kind ===
    "collectFromStorageLocation"
    ? purchaseRequestData.purchaseRequestDeliveryOption.value.incoterms
    : ensure(
        purchaseRequestData.purchaseRequestDeliveryOption.value.deliveryDetails
          .deliveryIncoterms,
      );
}

const finalStates: PurchaseRequestState[] = [
  "purchaserPaid",
  "purchaserRejected",
  "cancelled",
  "organisationPaid",
];

export function isPurchaseRequestFinalState(state: PurchaseRequestState) {
  return finalStates.includes(state);
}

export function formatDiscountPct(args: {
  afterDiscount: number | string;
  discount: number | string;
}): string {
  const { afterDiscount, discount } = args;
  const afterDiscountNum =
    typeof afterDiscount === "string" ? Number(afterDiscount) : afterDiscount;
  const discountNum =
    typeof discount === "string" ? Number(discount) : discount;

  const discountPct = (discountNum / (afterDiscountNum + discountNum)) * 100;
  const discountPct2dp = toPrecision(discountPct, 2);
  return (discountPct !== Number(discountPct2dp) ? "~" : "") + discountPct2dp;
}

export const calculatePurchaserNetSubtotal = (
  salePriceType: PurchaseRequestSalePriceType,
  netSubtotal: BigDecimal,
  dutyAndVat: DutyAndVat | null,
): string => {
  // If sale price is inc duty/vat then this is bundled into the net subtotal
  const addDuty =
    salePriceType === "incDutyAndVat" || salePriceType === "incDutyExVat";
  const addVat = salePriceType === "incDutyAndVat";

  return sumBigDecimal([
    netSubtotal,
    addDuty ? dutyAndVat?.duty : null,
    addVat ? dutyAndVat?.vat : null,
  ]);
};

export const calculatePurchaserTotalAmount = (
  netSubtotal: BigDecimal,
  dutyAndVat: DutyAndVat | null,
  deliveryCost: DeliveryFee | null,
  purchaserCoversDeliveryCost: boolean,
): string => {
  const numbers: (BigDecimal | undefined | null)[] = [
    netSubtotal,
    dutyAndVat?.duty,
    dutyAndVat?.vat,
  ];

  if (purchaserCoversDeliveryCost) {
    numbers.push(deliveryCost?.deliveryFee, deliveryCost?.vat);
  }

  return sumBigDecimal(numbers);
};

export const calculatePurchaserDeliveryCost = (
  deliveryFee: DeliveryFee | null,
  customDeliveryCost: AdditionalCost | null,
  additionalDeliveryCost: AdditionalCost | null,
  purchaserCoversDeliveryCost: boolean,
): DeliveryFee | null => {
  if (!purchaserCoversDeliveryCost) return null;

  const baseFee = customDeliveryCost
    ? { deliveryFee: customDeliveryCost.amount, vat: customDeliveryCost.vat }
    : deliveryFee;

  if (!baseFee && !additionalDeliveryCost) return null;

  return {
    deliveryFee: sumBigDecimal([
      baseFee?.deliveryFee,
      additionalDeliveryCost?.amount,
    ]),
    vat: sumBigDecimal([baseFee?.vat, additionalDeliveryCost?.vat]),
  };
};

const sumBigDecimal = (
  bigDecimals: (BigDecimal | undefined | null)[],
): BigDecimal => {
  const sum = bigDecimals
    .filter((v) => v !== null && v !== undefined && !Number.isNaN(Number(v)))
    .map((v) => Number(v))
    .reduce((a, b) => a + b, 0);
  return String(sum);
};

interface StatusChipConfig {
  displayText: string;
  variant: FeroChipVariant;
}

export const getPurchaseRequestStatusChipConfig = (
  status: PurchaseRequestState,
): StatusChipConfig => {
  switch (status) {
    case "new":
      return {
        displayText: "Awaiting Acceptance",
        variant: "info",
      };
    case "readyForCollection":
      return {
        displayText: "Awaiting Collection",
        variant: "info",
      };
    case "purchaserInvoiced":
      return {
        displayText: "Invoice Sent",
        variant: "info",
      };
    case "purchaserPaid":
      return {
        displayText: "Payment Received",
        variant: "success",
      };
    case "organisationPaid":
      return {
        displayText: "Final Receivable Paid",
        variant: "success",
      };
    case "productionOrderComplete":
      return {
        displayText: "Production Order Complete",
        variant: "info",
      };
    case "purchaserRejected":
      return {
        displayText: "Order Rejected",
        variant: "pending",
      };
    case "cancelled":
      return {
        displayText: "Order Cancelled",
        variant: "pending",
      };
    default:
      return {
        displayText: "Unknown Order Status",
        variant: "info",
      };
  }
};
